import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { makeGetTheme } from '../selectors/app-props';
import { makeGetCurrentUser } from '../selectors/users';
import { makeGetSortedActiveTeams, makeGetCurrentTeam } from '../selectors/teams';
import { makeGetPanelNotificationsCount } from '../selectors/notifications';

import { updateCurrentUser } from '../actions/users';
import { showModal } from '../actions/modal';
import { openFlyout } from '../actions/flyout';

import config from '../config';
import { trackEvent } from '../services/events-tracker-service';
import { featureAvailable } from '../services/features-service';
import { getSessionLength, logout, switchTeam, openIntercom } from '../globals-service';

import CanUser from './can-user';
import Popout from './popout';
import AvatarBase from './avatar-base';
import Tooltip from './tooltip';
import FlyoutToggleItem from './flyout-toggle-item';
import Toggle from './toggle';

import IconUpload from '../icons/icon-upload';
import IconPlus from '../icons/icon-plus';
import IconArrowDown from '../icons/icon-arrow-down';
import IconSearch from '../icons/icon-search';
import IconActivity from '../icons/icon-activity';
import IconSun from '../icons/icon-sun';

const TeamEntry = ({ team, loginRestrictedSSO, onClick, onEditClick }) => {
    const handleClick = () => {
        if (loginRestrictedSSO) {
            return;
        }

        onClick(team.url);
    }

    const handleEditClick = () => {
        if (loginRestrictedSSO) {
            return;
        }

        onEditClick(team.is_current ? 'current' : team.id);
    }

    return (
        <Tooltip
            disabled={!loginRestrictedSSO}
            placement="right"
            tooltip="You must login via SSO to access this team."
        >
            <div key={team.id} className={'inline-nav-item' + (team.is_current ? ' current' : '') + (loginRestrictedSSO ? ' disabled' : '')}>
                {
                    !team.is_current ? (
                        <div className="item-overlay" onClick={handleClick} />
                    ) : null
                }

                <div className="item-head">
                    <AvatarBase image={team.logo} color={team.color} secondaryColor={team.secondary_color} text={team.initials} solidImage={true} />
                </div>

                <div className="item-body">
                    {team.name}
                </div>

                <div className="item-foot">
                    <button className="btn" onClick={handleEditClick}>
                        <i className="ion ion-ios-gear" />
                    </button>
                </div>
            </div>
        </Tooltip>
    )
}


class SettingsCompact extends Component {
    state = {
        popoutVisible: false
    };

    handleActivateClick = () => {
        showModal('SWITCH_PLAN', {
            step: 'billing'
        });
    }

    handleAccountSettingsClick = () => {
        this.props.history.push({
            pathname: '/settings/profile',
            state: {
                closeUrl: this.props.history.getCurrentLocation().pathname
            }
        });

        this.closePopout();
    }

    handleToggleClick = () => {
        if (this.state.popoutVisible) {
            this.closePopout();
        } else {
            this.openPopout();
        }
    }

    doLogout = () => {
        trackEvent('USER.LOGOUT', {
            sessionLength: getSessionLength() / 1000 / 60
        }).then(logout);
    }

    handlePopoutShow = () => {
        this.setState({
            popoutVisible: true
        });
    }

    handlePopoutHide = () => {
        this.setState({
            popoutVisible: false
        });
    }

    handleShootUsMessageClick = () => {
        openIntercom();
    }

    handleImportProjectsClick = () => {
        showModal('IMPORT_PROJECTS');

        this.closePopout();
    }

    handleCreateTeamClick = () => {
        showModal('CREATE_TEAM');

        this.closePopout();
    }

    handleFlyoutToggleClick = flyoutId => {
        openFlyout(flyoutId);
    }

    handleThemeToggle = state => {
        const theme = state ? 'dark' : 'light';

        updateCurrentUser({
            theme,
            team_id: this.props.team.id
        });
    }

    handleEditTeamClick = teamId => {
        this.props.history.push({
            pathname: `/settings/team/${teamId}`,
            state: {
                closeUrl: this.props.history.getCurrentLocation().pathname
            }
        });

        this.closePopout();
    }

    handleTeamClick = teamUrl => {
        switchTeam({ teamUrl });
        this.closePopout();
    }

    openPopout() {
        this.popoutCtrl.show();
    }

    closePopout() {
        this.popoutCtrl.hide();
    }

    render() {
        if (!this.props.team) {
            return <div className="settings-compact" />
        }

        return (
            <div className={'settings-compact' + (this.state.popoutVisible ? ' popout-visible' : '')}>
                <div className="settings-compact-main" onClick={this.handleToggleClick}>
                    <div className="image">
                        <AvatarBase size="small" text={this.props.team.initials} image={this.props.team.logo} color={this.props.team.color} secondaryColor={this.props.team.secondary_color} />
                    </div>

                    <div className="settings-compact-content">
                        <span className="title">{this.props.team.name}</span>

                        <span className="arrow">
                            <IconArrowDown />
                        </span>
                    </div>
                </div>

                <FlyoutToggleItem
                    key="activity"
                    id="activity"
                    icon={<IconActivity />}
                    label="Notifications"
                    tooltipPlacement="bottom"
                    notifications={this.props.notificationsCount}
                    onClick={this.handleFlyoutToggleClick}
                />

                <Popout
                    className="no-arrow dark settings-compact-popout"
                    xAlign="left"
                    resizeToFit={true}
                    provideController={ctrl => {this.popoutCtrl = ctrl}}
                    onShow={this.handlePopoutShow}
                    onHide={this.handlePopoutHide}
                >
                    <div className="popout-main">
                        <div className="popout-section">
                            <div className="section-head">
                                <h4>profile</h4>
                            </div>

                            <div className="section-body popout-section-profile">
                                <div className="inline-nav-item current">
                                    <div className="item-head">
                                        <AvatarBase image={this.props.currentUser.avatar} color={this.props.currentUser.color} secondaryColor={this.props.currentUser.secondary_color} text={this.props.currentUser.initials} />
                                    </div>

                                    <div className="item-body">
                                        {this.props.currentUser.name}
                                    </div>

                                    <div className="item-foot">
                                        <button className="btn" onClick={this.handleAccountSettingsClick}>
                                            <i className="ion ion-ios-gear" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="popout-section">
                            <div className="section-head">
                                <h4>Teams</h4>
                            </div>

                            <div className="section-body popout-section-teams">
                                {
                                    this.props.teams.map((team, index) => <TeamEntry
                                        key={team.id || index}
                                        team={team}
                                        loginRestrictedSSO={team.sso_only && !this.props.me.sso && this.props.me.id !== team.owner}
                                        onClick={this.handleTeamClick}
                                        onEditClick={this.handleEditTeamClick}
                                    />)
                                }
                            </div>
                        </div>

                        <div className="popout-section popout-section-actions">
                            <div className="actions-group">
                                <div className="action-item-slim">
                                    <div className="action-item-icon">
                                        <IconSun />
                                    </div>

                                    <div className="action-item-body">
                                        Switch to Dark Mode
                                    </div>

                                    <div className="action-item-foot">
                                        <Toggle size="small" checked={this.props.theme === 'dark'} onChange={this.handleThemeToggle} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="popout-section popout-section-actions">
                            <CanUser type="createProject">
                                <div className="actions-group">
                                    {
                                        !featureAvailable('import') ? (
                                            <Tooltip tooltip="Import is not available on Free plan">
                                                <div style={{ width: '100%' }}>
                                                    <button className="btn btn-simple btn-small large-text muted" style={{ width: '100%' }} disabled>
                                                        <IconUpload color="#00a99b" size="14px" />

                                                        Import Projects
                                                    </button>
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <button className="btn btn-simple btn-small large-text" onClick={this.handleImportProjectsClick}>
                                                <IconUpload color="#00a99b" size="14px" />

                                                Import Projects
                                            </button>
                                        )
                                    }
                                </div>
                            </CanUser>

                            <div className="actions-group">
                                <button className="btn btn-simple btn-small large-text" onClick={this.handleCreateTeamClick}>
                                    <IconPlus color="#00a99b" size="12px" />

                                    Create New Team
                                </button>
                            </div>

                            <div className="actions-group">
                                <a className="btn btn-simple btn-small large-text" href="https://changelog.niftypm.com/" target="_blank" rel="noopener noreferrer">
                                    <span role="img" aria-label="stars">✨</span>&nbsp;
                                    What’s new
                                </a>

                                <button className="btn btn-simple btn-small large-text" onClick={this.doLogout}>
                                    <i className="icon material-icons">power_settings_new</i>

                                    Sign out
                                </button>
                            </div>
                        </div>

                        <div className="popout-section help-ad">
                            <div className="help-ad-icon">
                                <i className="ico-lego-head" />
                            </div>

                            <div className="help-ad-body">
                                <h5>Need help with something?</h5>

                                {
                                    config.BILLING_ENABLED ? (
                                        <p>
                                            We’re always here for you. <a onClick={this.handleShootUsMessageClick}>Shoot us a message</a> or read through <a href="http://help.niftypm.com" target="_blank" rel="noopener noreferrer">Tutorials</a>.
                                        </p>
                                    ) : (
                                        <p>
                                            We’re always here for you. <a onClick={this.handleShootUsMessageClick}>Shoot us a message</a>.
                                        </p>
                                    )
                                }

                            </div>
                        </div>
                    </div>
                </Popout>
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getCurrentTeam = makeGetCurrentTeam();
    const getSortedActiveTeams = makeGetSortedActiveTeams();
    const getPanelNotificationsCount = makeGetPanelNotificationsCount();
    const getTheme = makeGetTheme();

    return ({ teams, users, notifications, appProps }, ownProps) => {
        const team = getCurrentTeam(teams);
        return {
            team,
            teams: getSortedActiveTeams(teams.items),
            currentUser: getCurrentUser(users),
            notificationsCount: getPanelNotificationsCount(notifications.items),
            theme: getTheme(appProps)
        }
    };
}

export default connect(makeMapStateToProps)(SettingsCompact);
